import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';

import type { CalendarPrintSource, PrintDocument } from './types';

interface CalendarPrintContextValue {
  source: CalendarPrintSource | null;
  setSource: (source: CalendarPrintSource | null) => void;
  job: PrintDocument | null;
  setJob: (job: PrintDocument | null) => void;
  clearJob: () => void;
}

const CalendarPrintContext = createContext<CalendarPrintContextValue | null>(null);

interface CalendarPrintProviderProps {
  children: ReactNode;
}

/**
 * Holds what the current Calendar view would print (`source`) and the sheet being printed (`job`).
 * The layout owns the header button and the sheet; each view registers its own source.
 */
export function CalendarPrintProvider({ children }: CalendarPrintProviderProps): JSX.Element {
  const [source, setSource] = useState<CalendarPrintSource | null>(null);
  const [job, setJob] = useState<PrintDocument | null>(null);
  const clearJob = useCallback(() => setJob(null), []);

  const value = useMemo(
    () => ({ source, setSource, job, setJob, clearJob }),
    [source, job, clearJob]
  );

  return <CalendarPrintContext.Provider value={value}>{children}</CalendarPrintContext.Provider>;
}

export function useCalendarPrintContext(): CalendarPrintContextValue {
  const context = useContext(CalendarPrintContext);
  if (!context) {
    throw new Error('useCalendarPrintContext must be used within CalendarPrintProvider');
  }
  return context;
}

/**
 * Lets a Calendar view publish what "print the current view" means to the layout. This effect is
 * the child-to-ancestor-store sync: it registers on mount or change and clears on unmount. Callers
 * must memoise `source`, otherwise every render re-registers.
 */
export function useRegisterPrintSource(source: CalendarPrintSource): void {
  const { setSource } = useCalendarPrintContext();

  useEffect(() => {
    setSource(source);
    return () => setSource(null);
  }, [setSource, source]);
}
